/**
 * hydrodynamicShockPipeline — a Mach cone in a compressible fluid.
 *
 * A supersonic object moving through a fluid creates a Mach cone
 * — a shockwave that propagates at the speed of sound. The Mach
 * number (the ratio of the object's speed to the speed of sound)
 * determines the cone's angle.
 *
 * Innovations:
 *   - The "object" is rendered as a small bright dot moving
 *     across the screen.
 *   - The shock fronts are drawn as nested cones, each one
 *     representing a different "wave" of pressure.
 *   - The density gradient behind the shock is visualized as
 *     a color gradient: blue (low density) → white (high
 *     density) → red (shock).
 *   - The flow field is shown as streamlines bending around the
 *     object.
 *   - Audio: bass drives the object's speed, mid controls the
 *     Mach number, high drives the shock intensity.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface HydrodynamicShockPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.04, 0.10, 0.18), vec3(0.01, 0.02, 0.05), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // A single object moving in a circle.
  float objSpeed = 0.7 + 0.3 * bass;
  vec2 objPos = vec2(
    sin(uTime * objSpeed) * 0.30,
    cos(uTime * objSpeed * 0.85) * 0.20
  );

  // Object velocity.
  vec2 objVel = vec2(
    cos(uTime * objSpeed) * objSpeed,
    -sin(uTime * objSpeed * 0.85) * 0.85 * objSpeed
  );
  vec2 objDir = normalize(objVel);

  // Mach number (controls cone angle).
  float mach = 1.5 + 0.8 * mid;
  // Cone half-angle: sin(angle) = 1/mach.
  float coneAngle = asin(clamp(1.0 / mach, 0.0, 1.0));

  // Compute the Mach cone as a triangle behind the object.
  // The cone extends from the object in the direction opposite to motion.
  // Length of the cone.
  float coneLen = 0.45;

  // Project the pixel onto the cone's axis (pointing backward).
  vec2 toObj = p - objPos;
  float along = -dot(toObj, objDir);  // positive behind
  vec2 perp = toObj - (-objDir) * along;
  float perpDist = length(perp);

  // The cone is a triangle. At distance along from object, the cone
  // has half-width = along * tan(coneAngle).
  float halfWidth = along * tan(coneAngle);
  // Check if pixel is inside the cone.
  bool inCone = along > 0.0 && along < coneLen && perpDist < halfWidth;
  // Distance to cone surface (for AA).
  float coneSurfaceD = perpDist - halfWidth;
  // Distance to the cone tip.
  float tipD = length(toObj);
  // Distance to the cone's two edges (left and right boundaries).
  float edgeD = abs(perpDist - halfWidth);

  // Inside the cone: a gradient from yellow-orange (close to object)
  // to deep red (far from object).
  if (inCone) {
    float alongN = along / coneLen;
    vec3 coneCol = mix(
      vec3(0.95, 0.85, 0.40),
      vec3(0.85, 0.30, 0.20),
      alongN
    );
    col = mix(col, coneCol, 0.55);
    // Cone edges: bright.
    float edgeMask = 1.0 - smoothstep(0.0, 0.003, edgeD);
    col = mix(col, vec3(0.95, 0.95, 0.7), edgeMask * 0.7);
  }

  // Outside the cone: clean undisturbed flow.
  // Density gradient: blue flow lines along the motion.
  // Project the pixel onto the flow direction.
  vec2 flowDir = objDir;  // flow follows the object
  float flowAlong = dot(toObj, flowDir);
  vec2 flowPerp = toObj - flowDir * flowAlong;
  float flowPerpD = length(flowPerp);
  // Streamlines: parallel lines in front of the object.
  for (int s = -3; s <= 3; s++) {
    float fs = float(s);
    float streamOffset = fs * 0.06;
    float streamD = abs(flowPerpD - streamOffset);
    float streamMask = 1.0 - smoothstep(0.0, 0.003, streamD);
    // Streamlines fade away from the object.
    float fadeAway = exp(-abs(flowAlong) * 2.0);
    col += vec3(0.4, 0.7, 0.95) * streamMask * 0.3 * fadeAway;
  }

  // Stacked shockwave rings inside the cone.
  for (int r = 0; r < 4; r++) {
    float fr = float(r);
    float ringAlong = 0.10 + fr * 0.10;
    float ringHalfW = ringAlong * tan(coneAngle);
    // The ring is at distance ringAlong from the object, with half-width ringHalfW.
    vec2 ringCenter = objPos + (-objDir) * ringAlong;
    // The ring is a line perpendicular to the cone axis.
    // Distance from p to the ring.
    vec2 toRing = p - ringCenter;
    float ringAlongD = abs(-dot(toRing, objDir) - ringAlong);  // not 0
    float ringPerpD = abs(length(toRing - (-objDir) * (-dot(toRing, objDir))) - ringHalfW);
    float ringMask = exp(-ringPerpD * 300.0) * exp(-ringAlongD * 20.0);
    col += vec3(0.95, 0.7, 0.4) * ringMask * 0.6;
  }

  // The object itself: a bright bullet shape.
  float objD = length(p - objPos);
  float objCore = 1.0 - smoothstep(0.015, 0.025, objD);
  col += vec3(0.95, 0.95, 1.0) * objCore * 1.5;
  // Object streak (motion blur).
  for (int k = 0; k < 8; k++) {
    float fk = float(k) / 7.0;
    vec2 streakPt = objPos - objDir * fk * 0.03;
    float streakD = length(p - streakPt);
    col += vec3(0.95, 0.85, 0.5) * exp(-streakD * 100.0) * 0.6 * (1.0 - fk);
  }

  // Velocity arrow.
  vec2 arrEnd = objPos + objDir * 0.12;
  vec2 arrDir = arrEnd - objPos;
  float arrD = 1e9;
  for (int k = 0; k < 20; k++) {
    float fk = float(k) / 19.0;
    vec2 pt = objPos + arrDir * fk;
    float d = length(p - pt);
    arrD = min(arrD, d);
  }
  col += vec3(0.85, 0.95, 0.7) * exp(-arrD * 200.0) * 0.6;

  // Mach number indicator.
  vec2 mnPos = vec2(-0.78, 0.42);
  float mnD = length(p - mnPos);
  if (mnD < 0.025) {
    col = mix(col, vec3(0.7, 0.85, 1.0), 0.6);
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeHydrodynamicShockBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeHydrodynamicShockRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createHydrodynamicShockPipeline(gl: WebGL2RenderingContext): HydrodynamicShockPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawHydrodynamicShock(
  gl: WebGL2RenderingContext,
  pipe: HydrodynamicShockPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
